/* =========================================================
   product-360.js
   Frame 0 is a static "poster" photo. The 360 spin uses
   frames 1+ and starts when the visitor taps "View 360°".
   ========================================================= */

class Spin360 extends HTMLElement {
  connectedCallback() {
    this.frames = Array.from(this.querySelectorAll('.spin-360__frame'));
    this.frameCount = this.frames.length;
    this.current = 0;
    this.dragging = false;
    this.startX = 0;
    this.startFrame = 0;

    // Higher number = slower / less sensitive drag (more px needed per frame step).
    this.sensitivity = parseInt(this.dataset.sensitivity || '14', 10);

    // Auto-rotate settings.
    this.autoplay = this.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed || '110', 10); // ms between frames
    this.autoplayResumeDelay = parseInt(this.dataset.autoplayResumeDelay || '1500', 10); // ms after release
    this.autoplayTimer = null;
    this.resumeTimer = null;

    if (this.frameCount === 0) return;

    // Frame 0 is the poster; the spin only uses frames 1+.
    this.firstSpin = this.frameCount > 2 ? 1 : 0;
    this.poster = this.dataset.poster === 'true' && this.frameCount > 2;
    this.toggleBtn = this.querySelector('.spin-360__toggle');

    this.frames[0].classList.add('is-active');
    this.preload();

    this.addEventListener('pointerdown', this.onDragStart.bind(this));
    window.addEventListener('pointermove', this.onDragMove.bind(this));
    window.addEventListener('pointerup', this.onDragEnd.bind(this));
    this.addEventListener('dragstart', function (e) { e.preventDefault(); });

    var self = this;
    if (this.toggleBtn) {
      // Stop the button press from starting a drag.
      this.toggleBtn.addEventListener('pointerdown', function (e) { e.stopPropagation(); });
      this.toggleBtn.addEventListener('click', function () {
        if (self.classList.contains('is-poster')) self.enterSpin();
        else self.exitSpin();
      });
    }

    if (this.poster) {
      this.classList.add('is-poster');
    } else if (this.autoplay) {
      this.classList.add('is-autoplaying');
      this.startAutoplay();
    }
  }

  enterSpin() {
    this.classList.remove('is-poster');
    if (this.toggleBtn) this.toggleBtn.textContent = 'Back to photo';
    this.showFrame(this.firstSpin);
    if (this.autoplay) {
      this.classList.add('is-autoplaying');
      this.startAutoplay();
    }
  }

  exitSpin() {
    this.stopAutoplay();
    if (this.resumeTimer) clearTimeout(this.resumeTimer);
    this.frames[this.current].classList.remove('is-active');
    this.frames[0].classList.add('is-active');
    this.current = 0;
    this.classList.add('is-poster');
    if (this.toggleBtn) this.toggleBtn.textContent = 'View 360°';
  }

  preload() {
    var loadedCount = 0;
    var self = this;
    this.frames.forEach(function (img) {
      if (img.complete) {
        loadedCount++;
      } else {
        img.addEventListener('load', function () {
          loadedCount++;
          if (loadedCount === self.frameCount) self.classList.add('is-loaded');
        });
      }
    });
    if (loadedCount === this.frameCount) this.classList.add('is-loaded');
  }

  // Wraps within the spin frames only (poster at index 0 is excluded).
  showFrame(index) {
    var first = this.firstSpin;
    var n = this.frameCount - first;
    var normalized = ((((index - first) % n) + n) % n) + first;
    this.frames[this.current].classList.remove('is-active');
    this.frames[normalized].classList.add('is-active');
    this.current = normalized;
  }

  startAutoplay() {
    var self = this;
    this.stopAutoplay();
    this.autoplayTimer = setInterval(function () {
      self.showFrame(self.current + 1);
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  scheduleAutoplayResume() {
    if (!this.autoplay) return;
    var self = this;
    if (this.resumeTimer) clearTimeout(this.resumeTimer);
    this.resumeTimer = setTimeout(function () {
      self.startAutoplay();
    }, this.autoplayResumeDelay);
  }

  onDragStart(e) {
    // No dragging while the static poster photo is showing.
    if (this.classList.contains('is-poster')) return;
    this.dragging = true;
    this.startX = e.clientX;
    this.startFrame = Math.max(this.current, this.firstSpin);
    this.classList.add('has-interacted');
    this.stopAutoplay();
    if (this.resumeTimer) clearTimeout(this.resumeTimer);
    this.setPointerCapture && this.setPointerCapture(e.pointerId);
  }

  onDragMove(e) {
    if (!this.dragging) return;
    var deltaX = e.clientX - this.startX;
    var steps = Math.round(deltaX / this.sensitivity);
    this.showFrame(this.startFrame - steps);
  }

  onDragEnd() {
    if (!this.dragging) return;
    this.dragging = false;
    this.scheduleAutoplayResume();
  }
}

customElements.define('spin-360', Spin360);